import { ActionIcon, Box, Divider, Group, Menu, MenuItemProps, Paper, Stack, Text, Title } from "@mantine/core";
import { IconDots, IconLifebuoy } from "@tabler/icons-react";
import { ReactNode } from "@tanstack/react-router";
import { useIsSupport } from "shared/hooks/useIsSupport";

type DetailCardAction = { icon: ReactNode; label: string; onClick: (e: React.MouseEvent) => unknown } & Omit<MenuItemProps, "onClick">;

type DetailCardProps = {
    title: string;
    subtitle: ReactNode;
    badge?: ReactNode;
    actions?: DetailCardAction[];
    rightSection?: ReactNode;
    supportActions?: Omit<DetailCardAction, "icon">[];
    children: ReactNode;
};

export const DetailCard = ({ title, subtitle, rightSection, badge, children, actions, supportActions, ...rest }: DetailCardProps) => {
    const { isSupport } = useIsSupport();

    return (
        <Box w="100%" {...rest}>
            <Paper shadow="md" radius="md" px="sm" py="sm" w="100%">
                <Group justify="space-between" wrap="nowrap" align="flex-start" w="100%">
                    <Group style={{ rowGap: 0 }}>
                        <Title order={2} size="h3">
                            {title}
                        </Title>
                        {badge}
                    </Group>
                    <Group wrap="nowrap">
                        {rightSection}
                        {!!actions?.length && (
                            <Menu withInitialFocusPlaceholder={false}>
                                <Menu.Target>
                                    <ActionIcon variant="transparent" aria-label="Show message action">
                                        <IconDots size={16} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {actions.map(({ label, icon, onClick, ...rest }) => (
                                        <Menu.Item
                                            key={label}
                                            leftSection={icon}
                                            rightSection={
                                                <Text size="sm" c="inherit">
                                                    {label}
                                                </Text>
                                            }
                                            onClick={onClick}
                                            {...rest}
                                        />
                                    ))}
                                    {isSupport && !!supportActions && (
                                        <>
                                            <Menu.Divider />
                                            <Menu.Label>Support</Menu.Label>
                                            {supportActions?.map(({ label, onClick, ...rest }) => (
                                                <Menu.Item
                                                    key={label}
                                                    leftSection={<IconLifebuoy size={16} />}
                                                    rightSection={
                                                        <Text size="sm" c="inherit">
                                                            {label}
                                                        </Text>
                                                    }
                                                    onClick={onClick}
                                                    {...rest}
                                                />
                                            ))}
                                        </>
                                    )}
                                </Menu.Dropdown>
                            </Menu>
                        )}
                    </Group>
                </Group>

                <Stack gap={"xs"}>
                    <Text size="sm">{subtitle}</Text>
                    <Divider mx="-md" />
                    <Text lineClamp={4}>
                        {children || (
                            <Text inline size="sm" c="dimmed" fs="italic">
                                No Content
                            </Text>
                        )}
                    </Text>
                </Stack>
            </Paper>
        </Box>
    );
};
